import {
  SESSION_EXPIRED,
  SESSION_RESTORED,
  SIGN_OUT_SUCCESS,
} from '../modules/auth.js';
import {
  getRefreshTokenExpiry,
  removeAuthHeaders,
  AUTH_TOKEN_KEY,
  REFRESH_TOKEN_EXPIRY_KEY,
} from './session.js';

const WARN_BEFORE_MS = 5 * 60 * 1000;

// setTimeout's delay is a signed 32-bit int. Values > MAX_TIMEOUT silently
// fire ~immediately instead of after the requested delay. Refresh tokens can
// live longer than that (~30 days on prod), so schedule long delays by
// chaining setTimeout calls in MAX_TIMEOUT-sized steps.
const MAX_TIMEOUT = 2147483647; // 2^31 - 1, ~24.85 days

const setLongTimeout = (fn, delay) => {
  const handle = { id: null, cancelled: false };
  const schedule = (remaining) => {
    if (handle.cancelled) return;
    const step = remaining > MAX_TIMEOUT ? MAX_TIMEOUT : remaining;
    handle.id = setTimeout(() => {
      const left = remaining - step;
      if (left > 0) {
        schedule(left);
      } else {
        fn();
      }
    }, step);
  };
  schedule(delay);
  return handle;
};

const clearLongTimeout = (handle) => {
  if (!handle) return;
  handle.cancelled = true;
  if (handle.id != null) clearTimeout(handle.id);
};

let warningTimer = null;
let logoutTimer = null;
let signOutTimer = null;
let storageListenerAttached = false;

// --- helpers ---

const getStore = () => import('../store.js').then((m) => m.default);
const getNavigate = () => import('./router.js').then((m) => m.navigate);

const performLocalSignOut = async (
  message = 'Session expired. Please sign in again.'
) => {
  cancelSessionWarning();
  removeAuthHeaders();

  const store = await getStore();
  store.dispatch({ type: SIGN_OUT_SUCCESS });

  if (window.location.pathname !== '/') {
    const navigate = await getNavigate();
    navigate(`/?error=${encodeURIComponent(message)}`, { replace: true });
  }
};

// --- exports ---

export const scheduleSessionWarning = (refreshTokenExpiresAt) => {
  clearLongTimeout(warningTimer);
  clearLongTimeout(logoutTimer);
  warningTimer = null;
  logoutTimer = null;

  const expiresAt = new Date(refreshTokenExpiresAt).getTime();
  if (!expiresAt) return;

  const now = Date.now();
  const warnAt = expiresAt - now - WARN_BEFORE_MS;
  const logoutAt = expiresAt - now;

  if (warnAt > 0) {
    warningTimer = setLongTimeout(async () => {
      const store = await getStore();
      store.dispatch({ type: SESSION_EXPIRED });
    }, warnAt);
  } else if (logoutAt > 0) {
    getStore().then((store) => store.dispatch({ type: SESSION_EXPIRED }));
  }

  if (logoutAt > 0) {
    logoutTimer = setLongTimeout(() => performLocalSignOut(), logoutAt);
  }
};

export const cancelSessionWarning = () => {
  clearLongTimeout(warningTimer);
  clearLongTimeout(logoutTimer);
  warningTimer = null;
  logoutTimer = null;
};

export const restoreSessionWarning = () => {
  const expiresAt = getRefreshTokenExpiry();
  if (expiresAt) scheduleSessionWarning(expiresAt);
};

// --- listeners ---

const handleStorageChange = async (event) => {
  if (event.key === AUTH_TOKEN_KEY && event.newValue) {
    const store = await getStore();
    store.dispatch({ type: SESSION_RESTORED });
  }

  if (event.key === REFRESH_TOKEN_EXPIRY_KEY && event.newValue) {
    scheduleSessionWarning(event.newValue);
  }

  if (event.key === AUTH_TOKEN_KEY && event.newValue === null) {
    signOutTimer = setTimeout(() => performLocalSignOut(), 200);
  }
};

const handleVisibilityChange = async () => {
  if (document.visibilityState !== 'visible') return;

  const expiresAt = getRefreshTokenExpiry();
  if (!expiresAt) return;

  const timeLeft = new Date(expiresAt).getTime() - Date.now();

  if (timeLeft <= 0) {
    performLocalSignOut();
    return;
  }

  if (timeLeft <= WARN_BEFORE_MS) {
    const store = await getStore();
    store.dispatch({ type: SESSION_EXPIRED });
  }

  scheduleSessionWarning(expiresAt);
};

export const initCrossTabSync = () => {
  if (storageListenerAttached) return;
  storageListenerAttached = true;

  window.addEventListener('storage', handleStorageChange);
  document.addEventListener('visibilitychange', handleVisibilityChange);
};
